import { useTranslation } from "react-i18next";

import { LANG_STORAGE_KEY, SUPPORTED_LANGUAGES } from "@/i18n";
import type { SupportedLanguage } from "@/i18n";
import { useUpdateProfileMutation } from "@/queries/hooks/profileHooks";
import { cn } from "@/utils";

type Variant = "compact" | "card";

type LanguageToggleProps = {
  variant?: Variant;
  /** When true, also writes the choice to the user's profile (preferred_language). */
  persistToBackend?: boolean;
  className?: string;
};

function currentLang(lang: string): SupportedLanguage {
  return lang.startsWith("es") ? "es" : "en";
}

export function LanguageToggle({
  variant = "compact",
  persistToBackend = false,
  className,
}: LanguageToggleProps) {
  const { t, i18n } = useTranslation("landing");
  const updateProfile = useUpdateProfileMutation();
  const selected = currentLang(i18n.language);

  const setLanguage = async (lang: SupportedLanguage) => {
    if (lang === selected) return;
    await i18n.changeLanguage(lang);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      // localStorage may be unavailable (private mode, etc.); ignore.
    }
    if (persistToBackend) {
      // Fire-and-forget: profile update shouldn't block the language switch.
      // The mutation hook handles its own error toasts.
      updateProfile.mutate({ preferred_language: lang });
    }
  };

  const options: Array<{ value: SupportedLanguage; short: string; full: string }> = [
    {
      value: "en",
      short: t("nav.languageEnglishShort"),
      full: t("nav.languageEnglish"),
    },
    {
      value: "es",
      short: t("nav.languageSpanishShort"),
      full: t("nav.languageSpanish"),
    },
  ];

  if (variant === "compact") {
    return (
      <div
        role="radiogroup"
        aria-label={t("nav.languageLabel")}
        className={cn(
          "inline-flex items-center rounded-[10px] bg-lp-dark/5 border border-lp-border p-[3px]",
          className,
        )}
      >
        {options.map((opt) => {
          const isSelected = opt.value === selected;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => setLanguage(opt.value)}
              className={cn(
                "px-2.5 py-1 rounded-[7px] text-[13px] font-semibold transition-colors cursor-pointer",
                isSelected
                  ? "bg-gradient-lp-gold text-lp-dark"
                  : "text-lp-muted hover:text-lp-dark",
              )}
            >
              {opt.short}
            </button>
          );
        })}
      </div>
    );
  }

  // card variant: bigger picker for onboarding
  return (
    <div
      role="radiogroup"
      aria-label={t("nav.languageLabel")}
      className={cn(
        "grid grid-cols-2 gap-3 max-w-md",
        className,
      )}
    >
      {options.map((opt) => {
        const isSelected = opt.value === selected;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => setLanguage(opt.value)}
            disabled={
              persistToBackend &&
              updateProfile.isPending &&
              SUPPORTED_LANGUAGES.includes(opt.value)
            }
            className={cn(
              "text-left px-4 py-3 rounded-lg transition-colors font-semibold border-2",
              isSelected
                ? "bg-gold/40 text-garnet border-gold"
                : "hover:bg-accent/20 text-garnet/80 border-transparent bg-white",
              persistToBackend && updateProfile.isPending
                ? "opacity-60 cursor-not-allowed"
                : "cursor-pointer",
            )}
          >
            <div className="text-base">{opt.full}</div>
            <div className="text-xs text-garnet/60 mt-1">
              {opt.value.toUpperCase()}
            </div>
          </button>
        );
      })}
    </div>
  );
}
